import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import { ExpiryBadge } from '../inventory/expiry-badge';
import { formatQty } from '../inventory/format-qty';
import type { SearchProductCard } from '../inventory/types';
import { expiryColor, flattenHits, type LayoutSearchHit } from './layout-search-model';

/**
 * 【用户设计 2026-08-17】布局找货面板：搜索产品 → 批次清单按临期红→绿
 * 排序；点击行跳转到该位置所在场景并高亮。同一批次多处有货时先弹选择
 * （每处标数量）。命中位置的颜色同步喂给画布做矩形高亮。
 */
export function LayoutSearch({
  onActiveChange,
  onJump,
}: {
  /** 搜索清空时 → false：页面据此撤掉画布上的黄色高亮 */
  readonly onActiveChange: (active: boolean) => void;
  readonly onJump: (locationId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [chooser, setChooser] = useState<LayoutSearchHit | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, isFetching } = useQuery({
    queryKey: ['layout-search', query],
    queryFn: () =>
      apiFetch<{ items: SearchProductCard[] }>(`/search?q=${encodeURIComponent(query)}`),
    enabled: query.length > 0,
  });

  const hits = useMemo(() => flattenHits(data?.items ?? []), [data]);

  // 清空搜索 → 撤掉画布高亮（【用户裁决】临期颜色只在清单里，画布高亮只跟随点击）
  useEffect(() => {
    onActiveChange(query.length > 0);
  }, [query, onActiveChange]);

  const pick = (hit: LayoutSearchHit): void => {
    if (hit.locations.length === 1) {
      onJump(hit.locations[0]!.locationId);
    } else {
      // 用户原话："提醒我有两处地点有同一保质期（每个地点标出数量），让我选择去哪个"
      setChooser(hit);
    }
  };

  return (
    <div className="layout-search">
      <input
        className="field"
        style={{ width: '100%', marginBottom: 'var(--space-2)' }}
        placeholder={t('layout.searchPlaceholder')}
        value={input}
        onChange={(e) => setInput(e.target.value)}
      />
      {isFetching && <p className="field-hint">{t('app.loading')}</p>}
      {!isFetching && query.length > 0 && hits.length === 0 && (
        <p className="field-hint">{t('empty.noResults')}</p>
      )}

      {hits.map((hit) => (
        <button
          key={hit.key}
          type="button"
          className="layout-search__hit"
          style={{ borderLeftColor: expiryColor(hit.expirationDate, hit.hasExpiration) }}
          onClick={() => pick(hit)}
        >
          <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <strong>{hit.productName}</strong>
            <span className="field-hint" style={{ margin: 0 }}>
              {hit.batchNo}
            </span>
            <ExpiryBadge expirationDate={hit.expirationDate} hasExpiration={hit.hasExpiration} />
            <span>{formatQty(hit.onHandBase, hit.units)}</span>
          </span>
          {hit.locations.map((loc) => (
            <span key={loc.locationId} className="layout-search__path">
              {loc.warehouseName} › {loc.pathLabel}
            </span>
          ))}
        </button>
      ))}

      {chooser && (
        <div className="modal-overlay" onClick={() => setChooser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('layout.searchPickTitle')}</h2>
            <p className="field-hint">
              {t('layout.searchPickBody', {
                name: chooser.productName,
                batch: chooser.batchNo,
                count: chooser.locations.length,
              })}
            </p>
            {chooser.locations.map((loc) => (
              <button
                key={loc.locationId}
                type="button"
                className="layout-search__hit"
                style={{
                  borderLeftColor: expiryColor(chooser.expirationDate, chooser.hasExpiration),
                }}
                onClick={() => {
                  setChooser(null);
                  onJump(loc.locationId);
                }}
              >
                <span className="layout-search__path">
                  {loc.warehouseName} › {loc.pathLabel}
                </span>
                <span>{formatQty(loc.onHandBase, chooser.units)}</span>
              </button>
            ))}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setChooser(null)}>
                {t('app.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
